import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ListChecks, Loader2, MessageSquarePlus, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminButton } from '@/components/admin/ui';
import { useToast } from '@/hooks/use-toast';
import {
  addJobChecklistItem,
  applyTemplateToJob,
  clearJobChecklist,
  deleteJobChecklistItem,
  invalidateJobChecklists,
  useChecklistTemplates,
  useJobChecklist,
  useToggleChecklistItem,
} from '@/hooks/useChecklists';
import { checklistProgress, groupByRoom, type JobChecklistItem } from '@/lib/checklists';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';

interface JobChecklistProps {
  jobId: string;
  orgId: string | null;
  /** Assigned cleaner or manager: can tick items. */
  canTick: boolean;
  /** Owner / manager: can pick a checklist and add or remove items. */
  canEdit: boolean;
}

const addItemSchema = z.object({
  label: z.string().trim().min(1, 'Add what needs doing').max(300, 'Keep it under 300 characters'),
  room: z.string().trim().max(80, 'Keep the room name short').optional(),
});
type AddItemValues = z.infer<typeof addItemSchema>;

const noteSchema = z.object({ note: z.string().max(500, 'Keep notes under 500 characters') });
type NoteValues = z.infer<typeof noteSchema>;

function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error && typeof (error as { message: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  return 'Something went wrong. Please try again.';
}

function ItemNoteForm({ item, onSave, onCancel }: { item: JobChecklistItem; onSave: (note: string) => void; onCancel: () => void }) {
  const form = useForm<NoteValues>({ resolver: zodResolver(noteSchema), defaultValues: { note: item.note ?? '' } });
  return (
    <form
      className="flex items-center gap-2 px-3 pb-3"
      onSubmit={form.handleSubmit((values) => onSave(values.note))}
    >
      <Input
        autoFocus
        aria-label={`Note for ${item.label}`}
        placeholder="e.g. Oven needs a deep clean next time"
        maxLength={500}
        className="h-10 flex-1 text-[14px]"
        {...form.register('note')}
      />
      <Button type="submit" size="sm" className={adminButton.primary}>Save</Button>
      <Button type="button" size="sm" variant="ghost" className={adminButton.iconGhost} aria-label="Cancel note" onClick={onCancel}>
        <X />
      </Button>
    </form>
  );
}

export function JobChecklist({ jobId, orgId, canTick, canEdit }: JobChecklistProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: items = [], isLoading } = useJobChecklist(jobId);
  const { data: templates = [] } = useChecklistTemplates(canEdit ? orgId : null);
  const toggle = useToggleChecklistItem(jobId);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const addForm = useForm<AddItemValues>({ resolver: zodResolver(addItemSchema), defaultValues: { label: '', room: '' } });

  const progress = checklistProgress(items);
  const groups = groupByRoom(items);

  const refresh = () => invalidateJobChecklists(queryClient, jobId);

  const run = async (action: () => Promise<unknown>, success?: string) => {
    setBusy(true);
    try {
      await action();
      if (success) toast({ title: success });
      refresh();
    } catch (error) {
      toast({ title: 'Couldn’t update the checklist', description: errorMessage(error), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const onToggle = (item: JobChecklistItem) => {
    if (!canTick) return;
    toggle.mutate(
      { itemId: item.id, done: !item.done },
      { onError: (error) => toast({ title: 'Couldn’t tick that off', description: errorMessage(error), variant: 'destructive' }) },
    );
  };

  const onSaveNote = (item: JobChecklistItem, note: string) => {
    setNoteFor(null);
    toggle.mutate(
      { itemId: item.id, done: item.done, note },
      { onError: (error) => toast({ title: 'Couldn’t save the note', description: errorMessage(error), variant: 'destructive' }) },
    );
  };

  const onApplyTemplate = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;
    run(() => applyTemplateToJob(jobId, template, items.length), `Added “${template.name}”`);
  };

  const onAddItem = addForm.handleSubmit((values) =>
    run(async () => {
      await addJobChecklistItem(jobId, values.label, values.room || null, items.length);
      addForm.reset({ label: '', room: values.room ?? '' });
    }),
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading checklist…
      </div>
    );
  }

  if (items.length === 0 && !canEdit) return null;

  return (
    <section data-testid="job-checklist" aria-label="Checklist" className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-[14px] font-semibold">
          <ListChecks className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Checklist
        </h3>
        {items.length > 0 && (
          <span
            data-testid="job-checklist-progress"
            className={cn('text-[13px] font-semibold tabular-nums', progress.complete ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground')}
          >
            {progress.label} done
          </span>
        )}
      </div>

      {items.length > 0 && <Progress value={progress.percent} className="h-2" aria-label={`${progress.percent}% of the checklist done`} />}

      {items.length === 0 && canEdit && (
        <p className="text-[13px] text-muted-foreground">
          No checklist on this job yet. Pick one of your checklists or add items below.
        </p>
      )}

      <div className="space-y-3">
        {groups.map((group) => (
          <div key={group.room ?? '__none'} className="space-y-1.5">
            {group.room && (
              <p className="px-1 text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{group.room}</p>
            )}
            <ul className="overflow-hidden rounded-xl border border-border bg-card">
              {group.items.map((item) => (
                <li key={item.id} className="border-b border-border last:border-b-0">
                  <div className="flex items-stretch">
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={item.done}
                      aria-label={item.label}
                      disabled={!canTick}
                      onClick={() => onToggle(item)}
                      data-testid={`checklist-item-${item.id}`}
                      className={cn(
                        'flex min-h-[52px] flex-1 items-center gap-3 px-3 py-2.5 text-left transition-colors',
                        canTick ? 'hover:bg-muted/60 active:bg-muted' : 'cursor-default',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border-2 transition-colors',
                          item.done ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-muted-foreground/40 bg-background',
                        )}
                      >
                        {item.done && <Check className="h-4 w-4" strokeWidth={3} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={cn('block text-[14.5px] leading-snug', item.done && 'text-muted-foreground line-through')}>
                          {item.label}
                        </span>
                        {item.note && <span className="mt-0.5 block text-[12.5px] italic text-muted-foreground">“{item.note}”</span>}
                        {item.done && (item.done_by_name || item.done_at) && (
                          <span className="mt-0.5 block text-[11.5px] text-muted-foreground">
                            {[item.done_by_name, item.done_at ? format(parseISO(item.done_at), 'HH:mm, d MMM') : null].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </span>
                    </button>
                    {canTick && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-auto w-12 shrink-0 rounded-none text-muted-foreground hover:text-foreground"
                        aria-label={`Add a note to ${item.label}`}
                        onClick={() => setNoteFor(noteFor === item.id ? null : item.id)}
                      >
                        <MessageSquarePlus className="h-4 w-4" />
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-auto w-11 shrink-0 rounded-none text-muted-foreground hover:text-destructive"
                        aria-label={`Remove ${item.label}`}
                        disabled={busy}
                        onClick={() => run(() => deleteJobChecklistItem(item.id))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {noteFor === item.id && (
                    <ItemNoteForm item={item} onSave={(note) => onSaveNote(item, note)} onCancel={() => setNoteFor(null)} />
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {templates.length > 0 && (
              <Select value="" onValueChange={onApplyTemplate} disabled={busy}>
                <SelectTrigger className="h-9 w-auto min-w-[180px] text-[13px]" data-testid="job-checklist-apply" aria-label="Add a checklist">
                  <SelectValue placeholder={items.length ? 'Add another checklist' : 'Add a checklist'} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} ({t.items.length})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button type="button" size="sm" variant="outline" className={adminButton.outline} onClick={() => setShowAdd((v) => !v)}>
              <Plus /> Add item
            </Button>
            {items.length > 0 && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={adminButton.danger}
                disabled={busy}
                onClick={() => run(() => clearJobChecklist(jobId), 'Checklist removed')}
              >
                Remove checklist
              </Button>
            )}
          </div>
          {showAdd && (
            <form onSubmit={onAddItem} className="grid gap-2 sm:grid-cols-[1fr_140px_auto]">
              <Input aria-label="New item" placeholder="e.g. Wipe skirting boards" maxLength={300} {...addForm.register('label')} />
              <Input aria-label="Room (optional)" placeholder="Room (optional)" maxLength={80} {...addForm.register('room')} />
              <Button type="submit" size="sm" className={cn(adminButton.primary, 'h-10')} disabled={busy}>
                Add
              </Button>
              {addForm.formState.errors.label && (
                <p className="text-[12px] text-destructive sm:col-span-3">{addForm.formState.errors.label.message}</p>
              )}
            </form>
          )}
        </div>
      )}
    </section>
  );
}
